import type { UserCards } from '@/api/customer/getCustomerCardsQuery/types';
import type { BnplMethodType } from '@/utils/bnpl';
import {
  type PaymentMethod,
  PaymentMethodType,
  createBnplPaymentMethod,
  getBnplProvider,
  getTrackingMethod,
  isPaymentMethodBnpl,
  isPaymentMethodCardSaved,
  isSameBnplPaymentMethod,
} from '@/utils/paymentMethods';
import {
  LD_BNPL_SEEN_METHOD,
  LD_SEEN_PAYMENT_METHODS,
  type LdSeenPaymentMethod,
} from '@/utils/tracking/ldEvents';

const convertUserCardToPaymentMethod = (
  userCard: UserCards[0],
): PaymentMethod => {
  return {
    type: PaymentMethodType.card,
    id: userCard.id,
    card: userCard,
  };
};

const isUserCard = (m: PaymentMethod | UserCards[0]): m is UserCards[0] => {
  return 'last4' in m && 'brand' in m && !('type' in m);
};

/**
 * List key / testID. A control BNPL row is keyed by its provider so two rows
 * never collide on the shared `'bnpl'` id (SQR-15886). Treatment rows carry
 * several providers and keep the plain `'bnpl'` key.
 */
const getPaymentMethodKey = (method: PaymentMethod | UserCards[0]): string => {
  if (isUserCard(method)) return `card-${method.id}`;
  const provider = getBnplProvider(method);
  return provider ? `bnpl-${provider}` : method.id;
};

const BNPL_PROVIDER_LABEL_KEYS = {
  klarna: 'bnplProvider.klarna',
  affirm: 'bnplProvider.affirm',
  afterpay_clearpay: 'bnplProvider.afterpay_clearpay',
} as const satisfies Record<BnplMethodType, string>;

/**
 * The experiment's treatment arms render one provider-agnostic "Pay later"
 * row and let Stripe's PaymentElement pick the provider inside the sheet, so
 * the per-provider control rows collapse into a single row that carries every
 * eligible provider, at the position of the first one (SQR-15886).
 */
const collapseBnplRows = (
  methods: readonly (PaymentMethod | UserCards[0])[],
): (PaymentMethod | UserCards[0])[] => {
  const providers = methods.flatMap((method) =>
    !isUserCard(method) && isPaymentMethodBnpl(method) ? method.providers : [],
  );
  if (providers.length <= 1) return [...methods];

  let collapsed = false;
  return methods.flatMap((method) => {
    if (isUserCard(method) || !isPaymentMethodBnpl(method)) return [method];
    if (collapsed) return [];
    collapsed = true;
    return [createBnplPaymentMethod(providers)];
  });
};

const isPaymentMethodEqual = (
  a: PaymentMethod | null | undefined,
  b: PaymentMethod | UserCards[0] | null | undefined,
): boolean => {
  if (!a || !b) return false;

  const methodB: PaymentMethod =
    'type' in b && 'id' in b
      ? b
      : convertUserCardToPaymentMethod(b as UserCards[0]);

  if (a.type !== methodB.type) return false;
  if (a.id !== methodB.id) return false;
  if (isPaymentMethodBnpl(a)) return isSameBnplPaymentMethod(a, methodB);
  if (isPaymentMethodCardSaved(a) && isPaymentMethodCardSaved(methodB)) {
    return a.card.id === methodB.card.id;
  }
  return true;
};

const LD_SEEN_METHOD_LABELS = new Set<string>(LD_SEEN_PAYMENT_METHODS);

const isLdSeenMethod = (label: string): label is LdSeenPaymentMethod =>
  LD_SEEN_METHOD_LABELS.has(label);

/**
 * The control layout renders one row per provider; treatment arms render a
 * single provider-agnostic row. Either way this experiment only cares
 * whether BNPL was visible, not which provider backed it, so every BNPL row
 * still collapses to one `bnpl_seen` key. It's only reported seen once at
 * least one provider is actually eligible. Methods with no `_seen` key (e.g.
 * Stripe Link) are dropped.
 */
const toLdSeenMethods = (
  methods: readonly (PaymentMethod | UserCards[0])[],
  eligibleBnplMethods: BnplMethodType[],
): LdSeenPaymentMethod[] =>
  methods.flatMap((method) => {
    const paymentMethod = isUserCard(method)
      ? convertUserCardToPaymentMethod(method)
      : method;

    if (isPaymentMethodBnpl(paymentMethod)) {
      return eligibleBnplMethods.length > 0 ? [LD_BNPL_SEEN_METHOD] : [];
    }

    const label = getTrackingMethod(paymentMethod);
    return isLdSeenMethod(label) ? [label] : [];
  });

/**
 * The method to preselect: Apple Pay when the customer made it their default
 * (Cards screen) and it is offered, otherwise the saved card with `isDefault`
 * unless the API marks it expired (`valid: false`).
 */
const getDefaultPaymentMethod = (
  methods: (PaymentMethod | UserCards[0])[],
  applePayIsDefault: boolean,
): PaymentMethod | undefined => {
  const normalized = methods.map((method) =>
    isUserCard(method) ? convertUserCardToPaymentMethod(method) : method,
  );

  const applePay = applePayIsDefault
    ? normalized.find((method) => method.type === PaymentMethodType.applePay)
    : undefined;

  return (
    applePay ??
    normalized.find(
      (method) =>
        isPaymentMethodCardSaved(method) &&
        method.card.isDefault &&
        method.card.valid !== false,
    )
  );
};

export {
  BNPL_PROVIDER_LABEL_KEYS,
  collapseBnplRows,
  convertUserCardToPaymentMethod,
  getDefaultPaymentMethod,
  getPaymentMethodKey,
  isPaymentMethodEqual,
  isUserCard,
  toLdSeenMethods,
};
